import React from "react";
import { POSTS, getPost } from "./blog/posts";
import { Sprout, ArrowRight, ArrowLeft, Clock, Calendar } from "lucide-react";
import { useSEO } from "../lib/useSEO";

const fmtDate = (d) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

function Block({ b }) {
  switch (b.t) {
    case "h2":
      return <h2 className="mt-10 mb-3 font-heading text-2xl font-extrabold text-white">{b.c}</h2>;
    case "p":
      return <p className="mt-4 text-white/70 leading-relaxed">{b.c}</p>;
    case "ul":
      return <ul className="mt-4 space-y-2">{b.c.map((li, i) => (<li key={i} className="flex gap-2 text-white/70"><span className="text-ai mt-1">•</span><span>{li}</span></li>))}</ul>;
    case "ol":
      return <ol className="mt-4 space-y-2 list-decimal list-inside">{b.c.map((li, i) => (<li key={i} className="text-white/70 pl-1">{li}</li>))}</ol>;
    case "quote":
      return <blockquote className="mt-8 border-l-4 border-ai pl-5 py-1 text-lg font-heading font-semibold text-white/90 italic">{b.c}</blockquote>;
    case "stat":
      return <div className="mt-8 rounded-2xl border border-ai/25 bg-ai/[0.06] p-5 text-white/85 font-semibold">{b.c}</div>;
    default:
      return null;
  }
}

function Nav({ onHome, onGetStarted }) {
  return (
    <header className="sticky top-0 z-50 backdrop-blur-md bg-forest-900/85 border-b border-white/10">
      <div className="mx-auto max-w-7xl px-6 h-16 flex items-center justify-between">
        <button data-testid="blog-home" onClick={onHome} className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-md bg-ai flex items-center justify-center"><Sprout className="h-5 w-5" strokeWidth={2.4} /></div>
          <span className="font-heading font-extrabold text-xl">Assort<span className="text-ai">IQ</span></span>
        </button>
        <div className="flex items-center gap-3">
          <a href="/tools" className="text-sm font-semibold text-white/70 hover:text-white">Free tools</a>
          <a href="/compare" className="hidden sm:inline text-sm font-semibold text-white/70 hover:text-white">Compare</a>
          <a href="/blog" className="text-sm font-semibold text-white/70 hover:text-white">Resources</a>
          <button data-testid="blog-nav-cta" onClick={onGetStarted} className="group flex items-center gap-1.5 rounded-full bg-ai px-4 py-2 text-sm font-bold hover:bg-[#e05400] transition-all hover:-translate-y-0.5">
            Start free trial <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
          </button>
        </div>
      </div>
    </header>
  );
}

function Article({ post, onHome, onGetStarted }) {
  useSEO({
    title: `${post.title} | AssortIQ Resources`,
    description: post.description,
    canonical: `https://getassortiq.com/blog/${post.slug}`,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: post.title,
      description: post.description,
      image: post.cover,
      datePublished: post.date,
      keywords: post.keywords,
      author: { "@type": "Organization", name: "AssortIQ" },
      publisher: { "@type": "Organization", name: "AssortIQ" },
      mainEntityOfPage: `https://getassortiq.com/blog/${post.slug}`,
    },
  });
  return (
    <div className="min-h-screen bg-forest-900 text-white font-body antialiased">
      <Nav onHome={onHome} onGetStarted={onGetStarted} />
      <article className="mx-auto max-w-3xl px-6 py-12">
        <a href="/blog" data-testid="blog-back" className="inline-flex items-center gap-1.5 text-sm font-semibold text-white/60 hover:text-white"><ArrowLeft className="h-4 w-4" /> All resources</a>
        <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-ai/10 text-ai px-3 py-1 text-xs font-bold uppercase tracking-wider">{post.category}</div>
        <h1 className="mt-4 font-heading text-3xl sm:text-4xl font-extrabold leading-tight">{post.title}</h1>
        <div className="mt-4 flex items-center gap-4 text-sm text-white/45">
          <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4" /> {fmtDate(post.date)}</span>
          <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" /> {post.readMins} min read</span>
        </div>
        <img src={post.cover} alt={post.title} className="mt-8 w-full h-72 object-cover rounded-2xl border border-white/10" />
        <div className="mt-8">{post.body.map((b, i) => <Block key={i} b={b} />)}</div>

        <div className="mt-12 relative overflow-hidden rounded-3xl border border-ai/30 bg-gradient-to-br from-ai/20 to-transparent p-8 text-center">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(255,94,0,0.15),transparent_70%)]" />
          <div className="relative">
            <h3 className="font-heading text-2xl font-extrabold">See it on your own data</h3>
            <p className="mt-2 text-white/70">Upload a CSV or connect your POS and get explainable AI recommendations in the first session — free for 7 days.</p>
            <button data-testid="blog-article-cta" onClick={onGetStarted} className="mt-5 inline-flex items-center gap-2 rounded-full bg-ai px-6 py-3 text-sm font-bold uppercase tracking-wider hover:bg-[#e05400] transition-all hover:-translate-y-0.5">
              Start free trial <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </article>
      <footer className="border-t border-white/10">
        <div className="mx-auto max-w-3xl px-6 py-8 text-xs text-white/40">© {new Date().getFullYear()} AssortIQ. Built for grocery, retail & agriculture.</div>
      </footer>
    </div>
  );
}

function Index({ onHome, onGetStarted }) {
  useSEO({
    title: "Resources — Assortment, Inventory & Category Management Guides | AssortIQ",
    description: "Practical guides on SKU rationalization, reducing dead inventory and assortment optimization for independent and regional grocers, retailers and producers.",
    canonical: "https://getassortiq.com/blog",
  });
  return (
    <div className="min-h-screen bg-forest-900 text-white font-body antialiased">
      <Nav onHome={onHome} onGetStarted={onGetStarted} />
      <div className="mx-auto max-w-7xl px-6 py-14">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full bg-ai/10 text-ai px-3 py-1 text-xs font-bold uppercase tracking-wider">Resources</div>
          <h1 className="mt-4 font-heading text-4xl sm:text-5xl font-extrabold">Assortment & inventory playbooks</h1>
          <p className="mt-3 text-white/60">Practical, data-driven guides for operators who want to stock what sells and eliminate what doesn't — no data team required.</p>
        </div>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-grid">
          {POSTS.map((p) => (
            <a key={p.slug} href={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="group rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden hover:border-ai/40 hover:-translate-y-1 transition-all">
              <img src={p.cover} alt={p.title} className="w-full h-44 object-cover" />
              <div className="p-5">
                <div className="text-xs font-bold uppercase tracking-wider text-ai">{p.category}</div>
                <h2 className="mt-2 font-heading text-lg font-bold leading-snug group-hover:text-ai transition-colors">{p.title}</h2>
                <p className="mt-2 text-sm text-white/60 leading-relaxed">{p.excerpt}</p>
                <div className="mt-4 flex items-center gap-3 text-xs text-white/40">
                  <span>{fmtDate(p.date)}</span><span>·</span><span>{p.readMins} min read</span>
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>
      <footer className="border-t border-white/10">
        <div className="mx-auto max-w-7xl px-6 py-8 text-xs text-white/40">© {new Date().getFullYear()} AssortIQ. Built for grocery, retail & agriculture.</div>
      </footer>
    </div>
  );
}

export default function Blog({ slug, onHome, onGetStarted }) {
  const post = slug ? getPost(slug) : null;
  if (slug && post) return <Article post={post} onHome={onHome} onGetStarted={onGetStarted} />;
  return <Index onHome={onHome} onGetStarted={onGetStarted} />;
}
